"use client";

import React from "react";
import Link from "next/link";
import OurButton from "@/app/components/ourButton";
// import OurButton from "../components/ourButton";

interface MediaProps {
  type?: "video" | "image";
  src?: string;
  alt?: string;
}

interface FirstBlockAboutProps {
  tags?: string[];
  title?: string;
  buttonText?: string;
  buttonLink?: string;
  media?: MediaProps;
  highlightWords?: string[];
  primaryColor?: string;
}

const FirstBlockAbout: React.FC<FirstBlockAboutProps> = ({
  tags = [],
  title = "",
  buttonText = "",
  buttonLink = "",
  media = {},
  highlightWords = [],
  primaryColor = "#086CF2",
}) => {
  // Check if header has meaningful content
  const hasHeaderContent = title || tags.length > 0 || buttonText;

  // Check if media exists
  const hasMedia = media?.src;

  return (
    <section className="firstBlockAbout w-full flex flex-col items-center md:pb-6">
      {/* Header Section - Only render if it has content */}
      {hasHeaderContent && (
        <div className="text-center flex items-center flex-col pb-[50px]">
          <h2 className="max-w-[1100px] text-[2rem] sm:text-[3.15rem] md:text-[3.75rem] leading-none">
            {/* Logic to highlight words in the title */}
            {title.split(" ").map((word, index) => {
              return highlightWords.includes(word) ? (
                <span
                  key={index}
                  className="text-blue-500 text-[2rem] sm:text-[3.15rem] md:text-[3.75rem] font-bold"
                >
                  {word}{" "}
                </span>
              ) : (
                <span key={index}>{word} </span>
              );
            })}
          </h2>
          <div className="flex justify-center xl:justify-between items-center flex-wrap gap-2 md:gap-4 py-5">
            {tags.map((item) => (
              <Link
                key={item}
                href={`#${item.toLowerCase().replace(/\s+/g, "-")}`}
                className="uppercase underline group flex justify-end items-center gap-2.5 self-stretch px-3 py-1 rounded-md hover:bg-blue-50 hover:text-blue-500 transition-colors duration-200"
              >
                {item}
              </Link>
            ))}
          </div>
          <OurButton href={buttonLink} textForButton={buttonText} />
        </div>
      )}

      {/* Media Section - Only render if media exists */}
      {hasMedia && (
        <div className="relative w-full flex items-center justify-center">
          {/* Render background div only if media exists */}
          <div
            className="absolute top-1/2 transform -translate-y-1/2 w-[100%] h-[150px] opacity-25 md:h-[200px] rounded-xl"
            style={{ background: primaryColor}}
          ></div>

          {/* Media */}
          {media?.type === "video" ? (
            <video
              src={media?.src}
              autoPlay
              loop
              muted
              playsInline
              className="relative z-10 w-[90%] md:w-[80%] h-[250px] md:h-[400px] object-cover rounded-2xl"
            />
          ) : media?.type === "image" && media?.src ? (
            <img
              src={media?.src}
              alt={media?.alt || "Media content"}
              className="relative z-10 w-[90%] md:w-[80%] object-cover rounded-2xl"
              style={{ aspectRatio: "14 / 4" }}
            />
          ) : null}
        </div>
      )}
    </section>
  );
};

export default FirstBlockAbout;
